import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  continueListening,
  hideFromContinue,
  listLibrary,
  listSharedWithMe,
  type Book,
  type Resume,
} from "../api";
import { BookList, Cover } from "../components/BookList";
import { PlayIcon } from "../components/icons";
import Logo from "../components/Logo";
import ProgressBar from "../components/ProgressBar";
import { useT } from "../i18n";
import { useDismiss } from "../components/useDismiss";
import { useSession } from "../session";
import Landing from "./Landing";

/**
 * Library is the reader's own shelf: the books they saved, and the books they
 * started (ADR-0060).
 *
 * It used to be every published book on the platform, which reads as a library
 * only while one person is publishing. The catalogue moved to /browse, and this
 * page answers the other question — what am I reading? — with Continue
 * listening on top, where it already was.
 *
 * A signed-out visitor gets the landing page instead (ADR-0070): what this is,
 * and a few of the books their owners have offered publicly. They have no
 * library, and an empty shelf offering them one they cannot fill is a worse
 * front page — as was the whole catalogue, which told them nothing about where
 * they had landed.
 */
export default function Library() {
  const { isSignedIn, isPending: sessionPending } = useSession();
  const t = useT();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["library"],
    queryFn: () => listLibrary(),
    enabled: isSignedIn,
  });

  /**
   * Where you left off. This is what makes a library a reader's page rather
   * than a second copy of the studio: the studio is a list of things you are
   * making, this is a list of things you are part-way through.
   *
   * Signed-in only — a position has nowhere to live without an account.
   */
  const resume = useQuery({
    queryKey: ["continue"],
    queryFn: continueListening,
    enabled: isSignedIn,
    retry: false,
  });
  /**
   * One card, not three (ADR-0061). The books that used to sit under it as
   * small horizontal rows are on the shelf below, with a bar on each tile —
   * and they were on the shelf already, so the rows listed the same books
   * twice. What is left is the single thing a returning reader most likely
   * wants: the chapter they were in.
   */
  const removal = usePickUpRemoval();
  const hero = (resume.data?.items ?? []).find((r) => !r.completed && !removal.hides(r.book_id));

  /**
   * Books shared with a group this reader is in (ADR-0153). They are in no
   * catalogue, so without this list a reader would have to be sent a link to
   * find one. Shown only when there is something in it.
   */
  const shared = useQuery({
    queryKey: ["shared"],
    queryFn: () => listSharedWithMe(),
    enabled: isSignedIn,
    retry: false,
  });
  const sharedBooks = shared.data?.items ?? [];

  const books = data?.items ?? [];
  const bookOf = (id?: string) =>
    books.find((b) => b.id === id) ?? sharedBooks.find((b) => b.id === id);

  // Not while the session is still loading: showing a visitor the landing page
  // for a moment and then replacing it with their own shelf reads as a dropped
  // session, which is the rule the header already follows.
  //
  // The landing page, not the catalogue (ADR-0070): a stranger is told what
  // this is, and offered the books their owners have made free.
  // `/` is where a failed sign-in lands, so the notice goes on both branches:
  // every one of the redirect's reasons means no session was installed, but
  // declining a *second* sign-in while already holding one lands on the shelf.
  // A reader with nothing here at all — no book saved, started or shared with
  // them — starts on Browse, where the collections are (owner, 10-22). Only on
  // the page a load opened at `/` (a sign-in lands there), and once, so the
  // Library link still shows the empty shelf and its way to Browse.
  const navigate = useNavigate();
  const settled = !!data && resume.isSuccess && !shared.isPending;
  const toBrowse =
    settled &&
    !landing.decided &&
    books.length === 0 &&
    sharedBooks.length === 0 &&
    resume.data.items.length === 0;
  useEffect(() => {
    if (!settled || landing.decided) return;
    landing.decided = true;
    // A tap elsewhere renders as a transition, keeping this page mounted while
    // the next one draws; a read landing then must not replace where the
    // reader went (auth.spec's Settings, sent to Browse under load, 10-23).
    // The address has already changed, so it is the one asked.
    if (toBrowse && window.location.pathname === "/") navigate("/browse", { replace: true });
  }, [settled, toBrowse, navigate]);

  if (!sessionPending && !isSignedIn) return <Landing />;

  if (toBrowse) return null;

  return (
    <main className="mx-auto max-w-5xl px-4 pt-5 pb-12">
      {removal.pending && (
        <p
          role="status"
          data-testid="pick-up-removed"
          className="mb-4 flex items-center justify-between gap-3 rounded-card bg-surface px-4 py-3 text-sm"
        >
          <span>{t.library.removedFromPickUp(removal.pending.title)}</span>
          <button
            type="button"
            data-testid="pick-up-undo"
            onClick={removal.undo}
            className="shrink-0 font-semibold text-sage-deep"
          >
            {t.library.undo}
          </button>
        </p>
      )}

      {hero && (
        <section className="relative mb-8" data-testid="continue-shelf">
          <ResumeHero item={hero} book={bookOf(hero.book_id)} />
          <ResumeMenu onRemove={() => removal.remove(hero.book_id, hero.book_title)} />
        </section>
      )}

      {sharedBooks.length > 0 && (
        <section className="mb-8" data-testid="shared-with-you">
          <h2 className="mb-4 text-lg font-semibold tracking-tight">{t.library.sharedWithYou}</h2>
          <BookList
            books={sharedBooks}
            isPending={false}
            error={null}
            onRetry={() => void shared.refetch()}
            empty={null}
            progress
            testID="shared-book-list"
          />
        </section>
      )}

      {/* A section title, not a banner: the header already says where you
          are, and a page-high "Library" pushed the books below the fold. */}
      <h1 data-testid="library-heading" className="mb-4 text-lg font-semibold tracking-tight">
        {t.library.title}
      </h1>

      <BookList
        books={books}
        isPending={isPending || sessionPending}
        error={isError ? (error as Error) : null}
        onRetry={() => void refetch()}
        empty={<EmptyShelf />}
        progress
        testID="book-list"
      />
    </main>
  );
}

/** Whether this page load has had its say on where a reader starts. */
export const landing = {
  decided: typeof window !== "undefined" && window.location.pathname !== "/",
};

/** How long Undo is offered before the server hears of a removal. */
const UNDO_MS = 5_000;

/**
 * usePickUpRemoval takes a book out of Pick up where you left off (owner,
 * 10-22). The card goes at once; the server is told only when the Undo offer
 * ends without Undo (or the page is left), so Undo has nothing to put back.
 * The place itself is kept, and reading the book again brings it back.
 */
function usePickUpRemoval() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ bookId: string; title: string } | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latest = useRef(pending);
  latest.current = pending;

  const send = (bookId: string) => {
    setHidden((h) => new Set(h).add(bookId));
    void hideFromContinue(bookId)
      .then(() => queryClient.invalidateQueries({ queryKey: ["continue"] }))
      .catch(() =>
        setHidden((h) => {
          const next = new Set(h);
          next.delete(bookId);
          return next;
        }),
      );
  };
  const flush = () => {
    clearTimeout(timer.current);
    const p = latest.current;
    if (p) send(p.bookId);
    setPending(null);
  };
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      if (latest.current) void hideFromContinue(latest.current.bookId);
    },
    [],
  );

  return {
    pending,
    hides: (bookId?: string) => !!bookId && (hidden.has(bookId) || pending?.bookId === bookId),
    remove: (bookId: string | undefined, title: string) => {
      if (!bookId) return;
      flush();
      setPending({ bookId, title });
      latest.current = { bookId, title };
      timer.current = setTimeout(flush, UNDO_MS);
    },
    undo: () => {
      clearTimeout(timer.current);
      latest.current = null;
      setPending(null);
    },
  };
}

/** ResumeMenu is the resume card's "⋯": one action, out of the row. */
function ResumeMenu({ onRemove }: { onRemove: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, ref, () => setOpen(false));
  return (
    <div ref={ref} className="absolute end-2 top-2">
      <button
        type="button"
        data-testid="resume-menu"
        aria-label={t.library.pickUpMenu}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="grid size-8 place-items-center rounded-full bg-surface/80 text-ink-soft hover:text-ink"
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute end-0 z-10 mt-1 w-max max-w-xs rounded-card bg-surface p-1 border border-line shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            data-testid="remove-from-pick-up"
            onClick={() => {
              setOpen(false);
              onRemove();
            }}
            className="block w-full rounded-control px-3 py-2 text-start text-sm hover:bg-sage-soft"
          >
            {t.library.removeFromPickUp}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * EmptyShelf is a new reader's library, and it says which of the two ways a
 * book gets here — so the answer to "why is this empty?" is on the screen
 * rather than in the product's head. The way out is the catalogue.
 */
function EmptyShelf() {
  const t = useT();
  return (
    <div
      data-testid="empty-state"
      className="rounded-card border border-dashed border-line-strong p-10 text-center"
    >
      <Logo size={30} className="mx-auto opacity-40" />
      <p className="mt-3 text-base font-medium">{t.library.emptyTitle}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted">{t.library.emptyBody}</p>
      <Link
        to="/browse"
        data-testid="browse-books"
        className="mt-4 inline-flex rounded-control bg-sage px-3.5 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep"
      >
        {t.library.browseBooks}
      </Link>
    </div>
  );
}

/**
 * ResumeHero is the one thing a returning reader most likely wants: the
 * chapter they were in, over its book's cover, one tap from playing.
 *
 * It goes straight into the reader at the saved position — the whole point is
 * one tap back to listening, not one to a contents page and another to the
 * chapter.
 */
function ResumeHero({ item, book }: { item: Resume; book?: Book }) {
  const t = useT();
  const percent = Math.round(item.fraction * 100);
  const left = minutesLeft(item);
  return (
    <Link
      to={`/read/${item.chapter_id}`}
      data-testid="resume-card"
      className="group relative isolate block overflow-hidden rounded-card bg-surface shadow-[0_16px_40px_-24px_rgb(0_0_0/0.5)]"
    >
      {book?.cover_url && (
        <img
          src={book.cover_url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 size-full scale-150 object-cover opacity-60 blur-2xl saturate-150"
        />
      )}
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-surface/60" />
      <div className="flex items-center gap-4 p-4">
        <Cover book={book} className="w-20 shrink-0 shadow-lg" />
        <div className="min-w-0 flex-1">
          <p className="text-[0.7rem] font-semibold tracking-wide text-sage-deep uppercase">
            {t.library.pickUp}
          </p>
          <p className="mt-1 truncate text-base leading-snug font-semibold">
            <bdi>{item.chapter_title}</bdi>
          </p>
          <p className="truncate text-sm text-ink-soft">
            <bdi>{item.book_title}</bdi>
          </p>
          <ProgressBar
            percent={percent}
            label={t.library.percentThrough(percent, item.chapter_title)}
          />
          {left > 0 && <p className="mt-1 text-xs text-muted">{t.book.minutesLeft(left)}</p>}
        </div>
        <span className="grid size-12 shrink-0 place-items-center rounded-full bg-sage text-on-sage shadow-md transition-transform group-active:scale-95">
          <PlayIcon className="size-5" />
        </span>
      </div>
    </Link>
  );
}

/** minutesLeft is what the saved position implies is still to play. */
function minutesLeft(item: Resume): number {
  if (item.fraction <= 0) return 0;
  const left = item.position_ms / item.fraction - item.position_ms;
  return left > 0 ? Math.max(1, Math.round(left / 60_000)) : 0;
}
